export const SITE_NAME = "Parkinson's Insights"
export const DEFAULT_SITE_ORIGIN = 'https://parkinsonsinsights.com'
export const DEFAULT_PAGE_TITLE = SITE_NAME
export const DEFAULT_META_DESCRIPTION =
  "Evidence-informed Parkinson's resources, including news, expert webinars, practical hacks, and podcast episodes for patients and caregivers."

export const INDEXABLE_ROBOTS_CONTENT =
  'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
export const NOINDEX_ROBOTS_CONTENT = 'noindex,follow'

function normalizeHttpHost(url: URL): string {
  const normalizedHostname = url.hostname.toLowerCase()
  return url.port ? `${normalizedHostname}:${url.port}` : normalizedHostname
}

function normalizeConfiguredSiteUrl(url: string | undefined): string {
  if (!url) {
    return ''
  }

  const trimmed = url.trim()
  if (!trimmed) {
    return ''
  }

  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return ''
    }
    return `${parsed.protocol}//${normalizeHttpHost(parsed)}`
  } catch {
    return ''
  }
}

function normalizeAbsoluteHttpUrl(pathOrUrl: string): string {
  try {
    const parsed = new URL(pathOrUrl)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return pathOrUrl
    }
    const normalizedHost = normalizeHttpHost(parsed)
    return `${parsed.protocol}//${normalizedHost}${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return pathOrUrl
  }
}

const configuredSiteUrl = normalizeConfiguredSiteUrl(import.meta.env.VITE_SITE_URL)

export function getSiteOrigin(): string {
  if (configuredSiteUrl) {
    return configuredSiteUrl
  }
  return DEFAULT_SITE_ORIGIN
}

export function normalizePath(path: string): string {
  if (!path) {
    return '/'
  }
  return path.startsWith('/') ? path : `/${path}`
}

export function toAbsoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return normalizeAbsoluteHttpUrl(pathOrUrl)
  }
  const origin = getSiteOrigin()
  const path = normalizePath(pathOrUrl)
  if (!origin) {
    return path
  }
  return `${origin}${path}`
}

export function sanitizeForMeta(
  value: string | null | undefined,
  maxLength = 160,
): string {
  if (!value) {
    return ''
  }

  const normalized = value.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) {
    return normalized
  }
  return `${normalized.slice(0, maxLength - 1).trimEnd()}...`
}

export function hasTransientNoindexQuery(search: string): boolean {
  const params = new URLSearchParams(search)
  return params.has('authMode') || params.has('userProfile') || params.has('mode')
}
